import { memo, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

interface AnimatedCloudPoofProps {
  isActive?: boolean
  className?: string
}

const PARTICLE_GROUPS = [
  { x: '50%', y: '50%', rotation: 10 },
  { x: '33.33%', y: '20px', rotation: 75 },
  { x: '25%', y: '50%', rotation: 30 },
  { x: '33.33%', y: '8px', rotation: 250 },
  { x: '66.67%', y: '20px', rotation: 120 },
  { x: '75%', y: '50%', rotation: 320 },
  { x: '50%', y: '8px', rotation: 70 },
  { x: 'calc(100% - 20px)', y: '12px', rotation: 180 },
  { x: 'calc(100% - 8px)', y: '50%', rotation: 40 },
  { x: '16px', y: '50%', rotation: 90 },
] as const

export const AnimatedCloudPoof = memo<AnimatedCloudPoofProps>(({ isActive = true, className = '' }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '50px 0px',
  })

  // Only show animation when both isActive and inView are true
  const shouldAnimate = isActive && inView

  const particleConfigs = useMemo(() => {
    const NUM_PARTICLES = 20
    return Array.from({ length: NUM_PARTICLES }, (_, i) => {
      const angle = (i / NUM_PARTICLES) * 2 * Math.PI
      const distance = 8 + Math.random() * 8
      const size = 3 + Math.random() * 2.5
      const delay = i * 0.1
      const opacity = 0.15 + Math.random() * 0.12
      const duration = 2 + Math.random() * 2
      return { angle, distance, size, delay, opacity, duration }
    })
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && !document.getElementById('nav-chalky-poof-style')) {
      const style = document.createElement('style')
      style.id = 'nav-chalky-poof-style'
      style.innerHTML = `
        @keyframes nav-chalky-poof {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0) scale3d(0.5, 0.5, 1);
          }
          15% {
            opacity: var(--particle-opacity);
            transform: translate3d(calc(var(--poof-tx) * 0.3), calc(var(--poof-ty) * 0.3), 0) scale3d(0.8, 0.8, 1);
          }
          50% {
            opacity: calc(var(--particle-opacity) * 0.8);
            transform: translate3d(calc(var(--poof-tx) * 0.7), calc(var(--poof-ty) * 0.7), 0) scale3d(1, 1, 1);
          }
          100% {
            opacity: 0;
            transform: translate3d(var(--poof-tx), var(--poof-ty), 0) scale3d(1.2, 1.2, 1);
          }
        }

        @keyframes gradient-smoke {
          0% {
            opacity: 0;
            transform: scale3d(0.6, 0.6, 1) rotate(0deg);
          }
          25% {
            opacity: 0.4;
            transform: scale3d(1.2, 1.2, 1) rotate(1deg);
          }
          50% {
            opacity: 0.6;
            transform: scale3d(1.5, 1.5, 1) rotate(-1deg);
          }
          75% {
            opacity: 0.3;
            transform: scale3d(1.8, 1.8, 1) rotate(0.5deg);
          }
          100% {
            opacity: 0;
            transform: scale3d(2.2, 2.2, 1) rotate(0deg);
          }
        }

        .particle-group {
          will-change: opacity;
          contain: layout paint;
          isolation: isolate;
        }

        .particle {
          will-change: transform, opacity;
          backface-visibility: hidden;
          transform-style: preserve-3d;
          contain: layout paint;
        }

        .gradient-smoke {
          will-change: transform, opacity;
          backface-visibility: hidden;
          transform-style: preserve-3d;
          contain: layout paint;
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <motion.div
      ref={ref}
      className={`pointer-events-none absolute inset-0 -z-50 overflow-visible ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: shouldAnimate ? 1 : 0 }}
      transition={{ duration: 1.5, ease: 'easeInOut' }}
      style={{
        willChange: 'opacity',
        contain: 'layout paint',
      }}
    >
      {/* Gradient background - only render when in view */}
      {shouldAnimate && (
        <>
          <div
            className="gradient-smoke absolute inset-0 my-3 rounded-full"
            style={{
              background:
                'radial-gradient(ellipse 200% 150% at 50% 50%, rgba(206, 66, 59, 0.18) 0%, rgba(206, 66, 59, 0.12) 15%, rgba(206, 66, 59, 0.08) 30%, rgba(206, 66, 59, 0.04) 45%, rgba(206, 66, 59, 0.02) 60%, rgba(206, 66, 59, 0.008) 75%, transparent 90%)',
              animation: 'gradient-smoke 6s cubic-bezier(0.4, 0, 0.2, 1) infinite',
              filter: 'blur(3px)',
              animationDelay: '0s',
            }}
          />
          <div
            className="gradient-smoke absolute inset-0 mx-2 my-1 rounded-full"
            style={{
              background:
                'radial-gradient(ellipse 180% 120% at 40% 60%, rgba(206, 66, 59, 0.12) 0%, rgba(206, 66, 59, 0.08) 20%, rgba(206, 66, 59, 0.05) 35%, rgba(206, 66, 59, 0.025) 50%, rgba(206, 66, 59, 0.012) 65%, rgba(206, 66, 59, 0.005) 80%, transparent 95%)',
              animation: 'gradient-smoke 8s cubic-bezier(0.4, 0, 0.2, 1) infinite',
              filter: 'blur(2px)',
              animationDelay: '1s',
            }}
          />
          <div
            className="gradient-smoke absolute inset-0 m-1 rounded-full"
            style={{
              background:
                'radial-gradient(ellipse 160% 100% at 60% 40%, rgba(206, 66, 59, 0.08) 0%, rgba(206, 66, 59, 0.05) 25%, rgba(206, 66, 59, 0.03) 40%, rgba(206, 66, 59, 0.018) 55%, rgba(206, 66, 59, 0.008) 70%, rgba(206, 66, 59, 0.003) 85%, transparent 100%)',
              animation: 'gradient-smoke 10s cubic-bezier(0.4, 0, 0.2, 1) infinite',
              filter: 'blur(1.5px)',
              animationDelay: '2s',
            }}
          />
        </>
      )}

      {/* Particle groups - only render when in view */}
      {shouldAnimate &&
        PARTICLE_GROUPS.map((group, groupIdx) => (
          <div
            key={groupIdx}
            className="particle-group absolute inset-0"
            style={{
              transform: `rotate(${group.rotation}deg)`,
              transformOrigin: `${group.x} ${group.y}`,
            }}
          >
            {particleConfigs.map((particle, idx) => (
              <div
                key={idx}
                className="particle absolute rounded-full bg-[#CE423B]"
                style={
                  {
                    width: `${particle.size}px`,
                    height: `${particle.size}px`,
                    left: group.x,
                    top: group.y,
                    transform: 'translate3d(-50%, -50%, 0)',
                    opacity: 0,
                    '--particle-opacity': particle.opacity,
                    '--poof-tx': `${Math.cos(particle.angle) * particle.distance}px`,
                    '--poof-ty': `${Math.sin(particle.angle) * particle.distance}px`,
                    animation: `nav-chalky-poof ${particle.duration}s cubic-bezier(0.4, 0, 0.2, 1) ${particle.delay}s infinite`,
                    filter: 'blur(0.3px) contrast(1.2) saturate(1.5)',
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
        ))}
    </motion.div>
  )
})

AnimatedCloudPoof.displayName = 'AnimatedCloudPoof'
